import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { DocumentTextIcon, LockIcon } from './ui/Icons';
import {
  Button,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
} from './ui';
import { useApplicationSetting } from '../hooks/useApplicationSetting';
import { cn } from '../lib/utils';

export const QMS_EDITABLE_PASSWORD_KEY = 'qmsEditablePassword';

/**
 * Readonly Reference Document field with lock; unlock requires application-setting password.
 * Do not wrap in a nested <form> (renders inside FormDrawer).
 */
export function LockedReferenceDocumentField({
  value = '',
  onChange,
  isLoading = false,
  recordId,
  inputId = 'reference-document',
  emptyHint = 'No reference document found',
  placeholder = 'e.g. EN01-ΓΔ.13/08 - 2.0',
  canEdit: canEditProp,
}) {
  const { theme } = useTheme();
  const { value: editablePassword, isLoading: passwordSettingLoading } =
    useApplicationSetting(QMS_EDITABLE_PASSWORD_KEY);

  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const canEdit =
    canEditProp !== undefined ? Boolean(canEditProp) : typeof onChange === 'function';
  const showAsEditable = canEdit && isUnlocked;
  const hasValue = Boolean(value && String(value).trim());

  useEffect(() => {
    setIsUnlocked(false);
    setPasswordModalOpen(false);
    setPasswordInput('');
    setPasswordError('');
  }, [recordId]);

  const openUnlockModal = () => {
    setPasswordInput('');
    setPasswordError('');
    setPasswordModalOpen(true);
  };

  const closeUnlockModal = () => {
    setPasswordModalOpen(false);
    setPasswordInput('');
    setPasswordError('');
  };

  const handleUnlockSubmit = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (passwordSettingLoading) {
      setPasswordError('Loading password setting…');
      return;
    }
    const expected = editablePassword != null ? String(editablePassword) : '';
    if (!expected) {
      setPasswordError(
        `Password not configured (application setting "${QMS_EDITABLE_PASSWORD_KEY}").`,
      );
      return;
    }
    if (passwordInput !== expected) {
      setPasswordError('Incorrect password.');
      return;
    }
    setIsUnlocked(true);
    closeUnlockModal();
  };

  return (
    <>
      <div
        className={cn(
          'rounded-xl border p-4',
          theme.border.subtle,
          'bg-slate-50/80 dark:bg-slate-900/30',
        )}
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <DocumentTextIcon className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
            <h3 className={cn('text-base font-semibold truncate', theme.text.primary)}>
              Reference Document
            </h3>
          </div>
          {canEdit && !isLoading ? (
            showAsEditable ? (
              <button
                type="button"
                onClick={() => setIsUnlocked(false)}
                title="Lock field"
                aria-label="Lock reference document field"
                className={cn(
                  'inline-flex items-center gap-1.5 shrink-0 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
                  theme.border.light,
                  theme.text.secondary,
                  'hover:bg-white dark:hover:bg-slate-800',
                )}
              >
                <LockIcon className="h-4 w-4" />
                Lock
              </button>
            ) : (
              <button
                type="button"
                onClick={openUnlockModal}
                title="Unlock to edit (password required)"
                aria-label="Unlock reference document field"
                className="inline-flex items-center gap-1.5 shrink-0 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-blue-700 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
              >
                <LockIcon className="h-4 w-4" />
                Unlock
              </button>
            )
          ) : null}
        </div>

        {isLoading ? (
          <p className={cn('text-sm', theme.text.secondary)}>Loading reference document...</p>
        ) : (
          <div>
            <Input
              id={inputId}
              name="referenceDocument"
              type="text"
              value={value ?? ''}
              onChange={(e) => onChange?.(e.target.value)}
              readOnly={!showAsEditable}
              disabled={!showAsEditable}
              placeholder={showAsEditable ? placeholder : hasValue ? undefined : emptyHint}
              className={cn(
                'w-full max-w-xl',
                !showAsEditable && 'bg-white dark:bg-slate-950/40 cursor-default',
              )}
            />
            {!showAsEditable && hasValue ? (
              <p className={cn('text-xs mt-2', theme.text.muted)}>
                Locked — unlock with password to edit
              </p>
            ) : null}
            {showAsEditable ? (
              <p className={cn('text-xs mt-2', theme.text.muted)}>
                Editable — lock again when finished
              </p>
            ) : null}
          </div>
        )}
      </div>

      <Modal isOpen={passwordModalOpen} onClose={closeUnlockModal} size="sm">
        <ModalHeader onClose={closeUnlockModal}>
          <ModalTitle>Unlock Reference Document</ModalTitle>
          <ModalDescription>
            Enter the QMS editable password to edit this field.
          </ModalDescription>
        </ModalHeader>
        <ModalContent>
          <Label htmlFor={`${inputId}-password`} className="mb-1.5 block">
            Password
          </Label>
          <Input
            id={`${inputId}-password`}
            type="password"
            autoFocus
            value={passwordInput}
            onChange={(e) => {
              setPasswordInput(e.target.value);
              setPasswordError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                handleUnlockSubmit(e);
              }
            }}
            placeholder="Enter password"
            error={!!passwordError}
          />
          {passwordError ? (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">{passwordError}</p>
          ) : null}
        </ModalContent>
        <ModalFooter className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={closeUnlockModal}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleUnlockSubmit(e);
            }}
          >
            Unlock
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default LockedReferenceDocumentField;
